import React from "react";
import { useNavigate } from "react-router-dom";

import AppSidebarV2 from "../layout/AppSidebarV2";
import { useAuth } from "../../auth/authStore";
import { useNotifications } from "../../notifications/notificationsStore";
import { useToast } from "../../ui/toast/ToastProvider";
import { appointmentsApi } from "../../api/appointmentsApi";
import { diaryApi } from "../../api/diaryApi";
import { notificationsApi } from "../../api/notificationsApi";
import { onboardingApi } from "../../api/onboardingApi";
import { chatApi } from "../../api/chatApi";
import { plannerApi } from "../../api/plannerApi";
import { psychologistsApi } from "../../api/psychologistsApi";
import { blogApi } from "../../api/blogApi";
import { literatureApi } from "../../api/literatureApi";
import { podcastsApi } from "../../api/podcastsApi";
import { request } from "../../api/http";
import {
  DIARY_MOOD_OPTIONS,
  diaryMoodScoreFromId,
  diaryQuickEntryContent,
  diaryQuickEntryTitle,
} from "../../utils/diary";
import { openExternalUrl } from "../../utils/safeUrl";

const DONE_TEST_STATUSES = new Set(["COMPLETED", "DONE", "FINISHED", "PASSED"]);
const CANCELED_APPOINTMENT_STATUSES = new Set(["CANCELED", "CANCELLED", "REFUNDED", "DECLINED"]);
const TERMINAL_APPOINTMENT_STATUSES = new Set([
  ...CANCELED_APPOINTMENT_STATUSES,
  "CLOSED",
  "ENDED",
  "COMPLETED",
  "COMPLETE",
  "DONE",
  "FINISHED",
]);

function normalizeRole(roleRaw) {
  const role = String(roleRaw || "").trim();
  if (!role) return "CLIENT";
  return role.startsWith("ROLE_") ? role.slice(5) : role;
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function extractOnboardingFlags(raw) {
  const src = asObject(raw);
  const state = asObject(src.state);
  const dismissed = Boolean(src.dismissed ?? state.dismissed ?? false);
  const completed = Boolean(src.completed ?? state.completed ?? false);
  const shouldShowRaw = src.shouldShow ?? state.shouldShow;
  const shouldShow = typeof shouldShowRaw === "boolean" ? shouldShowRaw : !dismissed && !completed;
  return { dismissed, shouldShow };
}

function hasAnyEnabledNotificationSettings(raw) {
  const settings = asObject(raw);
  const values = Object.values(settings);
  return values.length > 0 ? values.some((value) => value === true) : false;
}

function buildClientOnboardingFallback({
  userName,
  primaryPsychologist,
  nextAppointment,
  latestPastAppointment,
  completedSessions,
  diaryEntriesCount,
  diaryAccessesCount,
  notificationsEnabled,
}) {
  const hasProfileName = Boolean(String(userName || "").trim()) && firstName(userName) !== "друг";
  const hasBookedSession = Boolean(nextAppointment || latestPastAppointment || Number(completedSessions) > 0);
  const steps = [
    {
      id: "profile_completed",
      title: "Заполните профиль",
      description: "Добавьте имя и базовую информацию — психологу будет проще подготовиться.",
      ctaLabel: "Открыть профиль",
      href: "/dashboard/settings?tab=profile",
      completed: hasProfileName,
      optional: false,
    },
    {
      id: "psychologist_selected",
      title: "Выберите психолога",
      description: "Найдите специалиста и выберите удобное время для первой встречи.",
      ctaLabel: "Найти психолога",
      href: "/psychologists",
      completed: Boolean(primaryPsychologist),
      optional: false,
    },
    {
      id: "first_session_booked",
      title: "Запишитесь на сессию",
      description: "После записи встреча появится в календаре и напоминаниях.",
      ctaLabel: "Открыть записи",
      href: "/dashboard/appointments",
      completed: hasBookedSession,
      optional: false,
    },
    {
      id: "first_diary_entry_created",
      title: "Создайте первую запись в дневнике",
      description: "Дневник помогает отслеживать состояние между сессиями.",
      ctaLabel: "Открыть дневник",
      href: "/dashboard/diary",
      completed: Number(diaryEntriesCount) > 0,
      optional: false,
    },
    {
      id: "diary_shared",
      title: "Поделитесь дневником",
      description: "Вы сами решаете, какие записи и какому психологу будут доступны.",
      ctaLabel: "Настроить доступ",
      href: "/dashboard/diary",
      completed: Number(diaryAccessesCount) > 0,
      optional: true,
    },
    {
      id: "notifications_enabled",
      title: "Включите уведомления",
      description: "Получайте напоминания о сессиях, сообщениях и новых материалах.",
      ctaLabel: "Открыть уведомления",
      href: "/notifications",
      completed: Boolean(notificationsEnabled),
      optional: true,
    },
  ];
  const requiredSteps = steps.filter((step) => !step.optional);
  const completedCount = requiredSteps.filter((step) => step.completed).length;
  const totalCount = requiredSteps.length;
  const completed = totalCount > 0 && completedCount >= totalCount;
  return {
    completedCount,
    totalCount,
    progressPercent: totalCount ? Math.round((completedCount / totalCount) * 100) : 100,
    completed,
    dismissed: false,
    shouldShow: !completed,
    nextStepId: steps.find((step) => !step.completed && !step.optional)?.id || steps.find((step) => !step.completed)?.id || null,
    steps,
  };
}

function normalizeClientOnboardingPayload(raw, fallback) {
  const src = asObject(raw);
  const rawSteps = Array.isArray(src.steps) ? src.steps.filter(Boolean) : [];
  const fallbackSteps = Array.isArray(fallback.steps) ? fallback.steps : [];
  const fallbackById = new Map(fallbackSteps.map((step) => [String(step.id || ""), step]));
  const apiById = new Map(rawSteps.map((step) => [String(step?.id || ""), step]));
  const fallbackIds = new Set(fallbackSteps.map((step) => String(step.id || "")));

  const steps = [
    ...fallbackSteps.map((step) => ({ ...step, ...asObject(apiById.get(String(step.id || ""))) })),
    ...rawSteps
      .filter((step) => step?.id && !fallbackIds.has(String(step.id)))
      .map((step) => ({ ...asObject(step) })),
  ].map((step) => {
    const base = fallbackById.get(String(step.id || "")) || {};
    return {
      ...base,
      ...step,
      completed: Boolean(step.completed || base.completed),
      optional: Boolean(step.optional ?? base.optional),
    };
  });

  const requiredSteps = steps.filter((step) => !step.optional);
  const fallbackCompleted = requiredSteps.filter((step) => step.completed).length;
  const fallbackTotal = requiredSteps.length;
  const completedCount = Number.isFinite(Number(src.completedCount)) ? Number(src.completedCount) : fallbackCompleted;
  const totalCount = Number.isFinite(Number(src.totalCount)) ? Number(src.totalCount) : fallbackTotal;
  const dismissed = Boolean(src.dismissed ?? fallback.dismissed);
  const shouldShowRaw = src.shouldShow;
  const shouldShow = typeof shouldShowRaw === "boolean" ? shouldShowRaw : !dismissed && completedCount < totalCount;

  return {
    completedCount,
    totalCount,
    progressPercent: Number.isFinite(Number(src.progressPercent))
      ? Number(src.progressPercent)
      : (totalCount ? Math.round((completedCount / totalCount) * 100) : 100),
    completed: Boolean(src.completed ?? fallback.completed ?? (totalCount > 0 && completedCount >= totalCount)),
    dismissed,
    shouldShow,
    nextStepId: String(
      src.nextStepId ||
      steps.find((step) => !step.completed && !step.optional)?.id ||
      steps.find((step) => !step.completed)?.id ||
      ""
    ).trim() || null,
    steps,
  };
}

function readEventStatus(raw) {
  const src = asObject(raw);
  const nested = asObject(src.sessionRoom || src.session_room || src.room || src.booking || src.appointment || src.session || src.conference);
  const statuses = [
    src.status,
    src.state,
    src.bookingStatus,
    src.booking_status,
    src.roomStatus,
    src.room_status,
    src.sessionStatus,
    src.session_status,
    nested.status,
    nested.state,
    nested.bookingStatus,
    nested.booking_status,
    nested.roomStatus,
    nested.room_status,
    nested.sessionStatus,
    nested.session_status,
  ].map((value) => String(value || "").trim().toUpperCase()).filter(Boolean);
  return statuses.find((status) => TERMINAL_APPOINTMENT_STATUSES.has(status)) || statuses[0] || "";
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item?.items?.content)) return obj.item.items.content;
  if (Array.isArray(obj.item)) return obj.item;
  return [];
}

function extractTotal(raw, fallbackItems = []) {
  const obj = asObject(raw);
  const fromRoot = Number(obj.totalElements);
  if (Number.isFinite(fromRoot)) return Math.max(0, fromRoot);
  const fromItem = Number(obj.item?.totalElements);
  if (Number.isFinite(fromItem)) return Math.max(0, fromItem);
  return fallbackItems.length;
}

function extractIdArray(raw) {
  return extractItems(raw)
    .map((x) => (x && typeof x === "object" ? x.id ?? x.userId ?? x.authorUserId ?? null : x))
    .map((x) => Number(x))
    .filter((x) => Number.isFinite(x) && x > 0);
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function dayKey(value) {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatRuDate(value, options) {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("ru-RU", options).format(date);
}

function formatRuTime(value) {
  return formatRuDate(value, { hour: "2-digit", minute: "2-digit" });
}

function diaryEntryTimestamp(entry = {}) {
  return (
    parseDate(entry.entryDate)?.getTime() ||
    parseDate(entry.updatedAt)?.getTime() ||
    parseDate(entry.createdAt)?.getTime() ||
    0
  );
}

function sortDiaryEntries(entries = []) {
  return [...entries].sort((a, b) => diaryEntryTimestamp(b) - diaryEntryTimestamp(a));
}

function formatInt(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return new Intl.NumberFormat("ru-RU").format(number);
}

function firstName(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || "друг";
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "BS";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function trimText(value, max = 180) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function mapAppointment(item = {}) {
  const raw = asObject(item);
  const startAt =
    raw.startDateTime ||
    raw.start_datetime ||
    raw.startAt ||
    raw.startsAt ||
    raw.dateTime ||
    raw.date ||
    raw.sessionAt ||
    null;
  const endAt = raw.endDateTime || raw.end_datetime || raw.endAt || raw.endsAt || raw.finishAt || null;
  const typeRaw = String(raw.type || raw.bookingType || raw.appointmentType || "").toUpperCase();
  const status = readEventStatus(raw) || String(raw.paymentStatus || "").toUpperCase();
  return {
    id: raw.id ?? raw.bookingId ?? raw.booking_id ?? raw.groupSessionId ?? null,
    title:
      raw.sessionTitle ||
      raw.groupSessionTitle ||
      raw.title ||
      raw.groupTitle ||
      raw.psychologistName ||
      raw.psychologist?.name ||
      "Сессия",
    type: typeRaw.includes("GROUP") ? "GROUP" : "INDIVIDUAL",
    status,
    startAt,
    endAt,
    amount: raw.amount ?? raw.price ?? raw.totalAmount ?? raw.priceAmount ?? null,
    telemostUrl:
      raw.telemostUrl || raw.videoRoomUrl || raw.videoUrl || raw.meetingUrl || raw.roomUrl || raw.sessionRoomUrl || "",
    psychologistName:
      raw.psychologistName || raw.psychologistFullName || raw.psychologist?.name || raw.withName || raw.specialistName || "",
    psychologistUserId:
      raw.psychologistUserId || raw.psychologistId || raw.psychologist?.userId || raw.psychologist?.id || null,
  };
}

function mapTestAssignment(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? raw.assignmentId ?? raw.testId ?? null,
    title: raw.title || raw.testTitle || raw.name || "Тест",
    status: String(raw.status || raw.state || "").toUpperCase(),
    deadlineAt: raw.deadlineAt || raw.dueAt || raw.expiresAt || raw.deadline || null,
    durationMinutes: Number(raw.durationMinutes || raw.estimatedMinutes || raw.duration || 0) || null,
    launchUrl:
      raw.launchUrl ||
      raw.openUrl ||
      raw.assignmentUrl ||
      raw.testUrl ||
      raw.url ||
      raw.externalUrl ||
      "",
  };
}

function mapPsychologist(raw = {}) {
  const item = asObject(raw);
  const methods = asArray(item.methods).map((method) => {
    if (typeof method === "string") return method;
    return method?.title || method?.name || "";
  });
  return {
    id: item.userId ?? item.id ?? null,
    name: item.name || item.fullName || "Психолог",
    headline: item.headline || item.specializationLabel || item.about || "",
    avatarUrl: item.avatarUrl || item.avatar || "",
    rating: Number(item.averageRating || item.rating || 0) || null,
    methods: methods.filter(Boolean),
    sessionsCount:
      Number(item.counts?.clientsCount || item.sessionsCount || item.reviewsCount || 0) || null,
  };
}

function mapRecommendationBook(item = {}) {
  const raw = asObject(item);
  const id = raw.id ?? raw.bookId ?? raw.slug ?? null;
  return {
    key: `book-${id || raw.title || ""}`,
    type: "Книга",
    title: raw.title || raw.name || "Книга",
    subtitle: raw.author || raw.authors || raw.psychologistName || "Литература",
    emoji: "📚",
    href: id ? `/literature/books/${id}` : "/literature",
  };
}

function mapRecommendationPodcast(item = {}) {
  const raw = asObject(item);
  const id = raw.id ?? raw.podcastId ?? raw.slug ?? null;
  return {
    key: `podcast-${id || raw.title || ""}`,
    type: "Подкаст",
    title: raw.title || raw.name || "Подкаст",
    subtitle: raw.authorName || raw.psychologistName || "Подкасты",
    emoji: "🎙️",
    href: id ? `/podcasts/${id}` : "/podcasts",
  };
}

function mapRecommendationBlog(item = {}) {
  const raw = asObject(item);
  const slug = raw.slug || raw.id || raw.postId || null;
  return {
    key: `blog-${slug || raw.title || ""}`,
    type: "Статья",
    title: raw.title || raw.name || "Статья",
    subtitle: raw.authorName || raw.psychologistName || "Блог",
    emoji: "✍️",
    href: slug ? `/blog/${slug}` : "/blog",
  };
}

function isExternalHref(href) {
  return /^https?:\/\//i.test(String(href || "").trim());
}

function MockIcon({ name, size = 15, strokeWidth = 1.8 }) {
  const commonProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  switch (name) {
    case "calendar":
      return (
        <svg {...commonProps}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4" />
          <path d="M8 3v4" />
          <path d="M3 10h18" />
        </svg>
      );
    case "chat":
      return (
        <svg {...commonProps}>
          <path d="M7 18l-4 3V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7z" />
        </svg>
      );
    case "note":
      return (
        <svg {...commonProps}>
          <path d="M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
          <path d="M14 3v5h5" />
        </svg>
      );
    case "users":
      return (
        <svg {...commonProps}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9.5" cy="7" r="3" />
          <path d="M20 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M15 4.13a3 3 0 0 1 0 5.74" />
        </svg>
      );
    case "bell":
      return (
        <svg {...commonProps}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    case "spark":
      return (
        <svg {...commonProps}>
          <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z" />
        </svg>
      );
    case "link":
      return (
        <svg {...commonProps}>
          <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L10 5" />
          <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07L14 19" />
        </svg>
      );
    default:
      return null;
  }
}

function MockSectionHeading({ icon, title }) {
  return (
    <div className="client-mock__heading">
      <span className="client-mock__heading-icon">
        <MockIcon name={icon} />
      </span>
      <h2>{title}</h2>
    </div>
  );
}

export default function ClientDashboardV2() {
  const navigate = useNavigate();
  const toast = useToast();
  const { me, logout } = useAuth();
  const { unreadNotificationsCount = 0, messageUnreadTotal = 0 } = useNotifications();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const toggleSidebarFromTopbar = React.useCallback(() => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches) {
      setMobileSidebarOpen(true);
      return;
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("burosoul:toggle-sidebar"));
    }
  }, []);

  const openSidebarEditorFromTopbar = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("burosoul:open-sidebar-editor"));
    }
  }, []);
  const [loading, setLoading] = React.useState(true);
  const [selectedMood, setSelectedMood] = React.useState("good");
  const [moodNote, setMoodNote] = React.useState("");
  const [savingMood, setSavingMood] = React.useState(false);
  const [savingTaskIds, setSavingTaskIds] = React.useState([]);
  const [onboardingExpanded, setOnboardingExpanded] = React.useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = React.useState(false);
  const [onboardingShouldShow, setOnboardingShouldShow] = React.useState(true);
  const [onboardingActionLoading, setOnboardingActionLoading] = React.useState(false);

  const [data, setData] = React.useState({
    appointments: [],
    notifications: [],
    chatDialogs: [],
    diaryEntries: [],
    onboarding: null,
    plannerTasks: [],
    testsAssigned: [],
    savedBooksTotal: 0,
    podcastSubscriptionsTotal: 0,
    recommendations: [],
    primaryPsychologist: null,
  });

  const userName = me?.name || me?.fullName || me?.login || me?.email || "Пользователь";
  const normalizedRole = normalizeRole(me?.role);
  const canManageLiterature = ["ADMIN", "PSYCHOLOGIST", "MODERATOR"].includes(normalizedRole);

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const currentUserName = me?.name || me?.fullName || me?.login || me?.email || "Пользователь";
      const currentNowTs = Date.now();

      const safe = async (fn, fallback) => {
        try {
          return await fn();
        } catch {
          return fallback;
        }
      };

      const [
        appointmentsRaw,
        notificationsRaw,
        diaryRaw,
        testsRaw,
        plannerRaw,
        savedBooksRaw,
        podcastSubscriptionsRaw,
        blogRaw,
        literatureRaw,
        podcastsRaw,
        subscriptionsRaw,
        favouritesRaw,
        dialogsRaw,
        diaryAccessesRaw,
        notificationSettingsRaw,
        onboardingRaw,
      ] = await Promise.all([
        safe(() => appointmentsApi.listMyAll(), []),
        safe(() => notificationsApi.list({ onlyUnread: false, limit: 4 }), { items: [] }),
        safe(() => diaryApi.listEntries({ page: 0, size: 50 }), { items: [] }),
        safe(() => request("/api/me/tests/assigned?page=0&size=20"), []),
        safe(() => plannerApi.listTasks({ page: 0, size: 50 }), { items: [] }),
        safe(() => request("/api/me/literature/saved?page=0&size=20"), []),
        safe(() => request("/api/me/podcasts/subscriptions?page=0&size=20"), []),
        safe(() => blogApi.list({ status: "PUBLISHED", page: 0, size: 3 }), []),
        safe(() => literatureApi.getLatest({ page: 0, size: 3 }), []),
        safe(() => podcastsApi.getCatalog({ page: 0, size: 3 }), []),
        safe(() => psychologistsApi.subscriptions(), []),
        safe(() => psychologistsApi.favourites(), []),
        safe(() => chatApi.listDialogs(), []),
        safe(() => diaryApi.listAccesses(), []),
        safe(() => notificationsApi.getSettings(), null),
        safe(() => onboardingApi.get(), null),
      ]);

      const appointments = extractItems(appointmentsRaw).map(mapAppointment).filter((item) => parseDate(item.startAt));
      const notifications = asArray(notificationsRaw?.items);
      const diaryEntries = sortDiaryEntries(asArray(diaryRaw?.items));
      const plannerTasks = asArray(plannerRaw?.items);
      const testsAssigned = extractItems(testsRaw).map(mapTestAssignment);

      const savedBooksItems = extractItems(savedBooksRaw);
      const podcastSubscriptionsItems = extractItems(podcastSubscriptionsRaw);

      const blogItems = extractItems(blogRaw);
      const literatureItems = extractItems(literatureRaw);
      const podcastsItems = extractItems(podcastsRaw);

      const recommendations = [
        ...literatureItems.slice(0, 1).map(mapRecommendationBook),
        ...podcastsItems.slice(0, 1).map(mapRecommendationPodcast),
        ...blogItems.slice(0, 1).map(mapRecommendationBlog),
      ];

      const psychologistIds = [...extractIdArray(subscriptionsRaw), ...extractIdArray(favouritesRaw)];

      let primaryPsychologist = null;
      if (psychologistIds.length) {
        const detail = await safe(() => psychologistsApi.get(psychologistIds[0]), null);
        if (detail) primaryPsychologist = mapPsychologist(detail);
      }

      if (!primaryPsychologist) {
        const firstWithPsych = appointments.find((item) => item.psychologistName);
        if (firstWithPsych) {
          primaryPsychologist = {
            id: firstWithPsych.psychologistUserId,
            name: firstWithPsych.psychologistName,
            headline: "Ваш специалист",
            avatarUrl: "",
            rating: null,
            methods: [],
            sessionsCount: null,
          };
        }
      }

      const onboardingFallback = buildClientOnboardingFallback({
        userName: currentUserName,
        primaryPsychologist,
        nextAppointment: appointments.find((item) => {
          const startsAt = parseDate(item.startAt)?.getTime() || 0;
          const status = String(item.status || "").toUpperCase();
          return startsAt > currentNowTs && !TERMINAL_APPOINTMENT_STATUSES.has(status);
        }) || null,
        latestPastAppointment: appointments
          .filter((item) => {
            const startsAt = parseDate(item.startAt)?.getTime() || 0;
            const status = String(item.status || "").toUpperCase();
            return startsAt > 0 && startsAt <= currentNowTs && !CANCELED_APPOINTMENT_STATUSES.has(status);
          })
          .sort((a, b) => (parseDate(b.startAt)?.getTime() || 0) - (parseDate(a.startAt)?.getTime() || 0))[0] || null,
        completedSessions: appointments.filter((item) => {
          const startsAt = parseDate(item.startAt)?.getTime() || 0;
          const status = String(item.status || "").toUpperCase();
          return startsAt > 0 && startsAt <= currentNowTs && !CANCELED_APPOINTMENT_STATUSES.has(status);
        }).length,
        diaryEntriesCount: diaryEntries.length,
        diaryAccessesCount: Array.isArray(diaryAccessesRaw) ? diaryAccessesRaw.length : 0,
        notificationsEnabled: hasAnyEnabledNotificationSettings(notificationSettingsRaw),
      });
      const onboarding = normalizeClientOnboardingPayload(onboardingRaw, onboardingFallback);
      const onboardingFlags = extractOnboardingFlags(onboardingRaw ?? onboarding);

      if (cancelled) return;

      setData({
        appointments,
        notifications,
        chatDialogs: extractItems(dialogsRaw).slice(0, 4),
        diaryEntries,
        onboarding,
        plannerTasks,
        testsAssigned,
        savedBooksTotal: extractTotal(savedBooksRaw, savedBooksItems),
        podcastSubscriptionsTotal: extractTotal(podcastSubscriptionsRaw, podcastSubscriptionsItems),
        recommendations,
        primaryPsychologist,
      });
      setOnboardingDismissed(onboardingFlags.dismissed);
      setOnboardingShouldShow(onboardingFlags.shouldShow);

      setLoading(false);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [me?.email, me?.fullName, me?.login, me?.name]);

  const appointmentsSorted = React.useMemo(() => {
    return [...data.appointments].sort((a, b) => {
      const aTime = parseDate(a.startAt)?.getTime() || 0;
      const bTime = parseDate(b.startAt)?.getTime() || 0;
      return aTime - bTime;
    });
  }, [data.appointments]);

  const nowTs = Date.now();
  const completedSessions = React.useMemo(() => {
    return appointmentsSorted.filter((item) => {
      const status = String(item.status || "").toUpperCase();
      const startsAt = parseDate(item.startAt)?.getTime() || 0;
      if (!startsAt || startsAt > nowTs) return false;
      if (CANCELED_APPOINTMENT_STATUSES.has(status)) return false;
      return true;
    }).length;
  }, [appointmentsSorted, nowTs]);

  const upcomingAppointments = React.useMemo(() => {
    return appointmentsSorted.filter((item) => {
      const startsAt = parseDate(item.startAt)?.getTime() || 0;
      const status = String(item.status || "").toUpperCase();
      if (TERMINAL_APPOINTMENT_STATUSES.has(status)) return false;
      return startsAt > nowTs;
    });
  }, [appointmentsSorted, nowTs]);

  const nextAppointment = upcomingAppointments[0] || null;
  const pastAppointments = React.useMemo(() => {
    return appointmentsSorted
      .filter((item) => {
        const startsAt = parseDate(item.startAt)?.getTime() || 0;
        const status = String(item.status || "").toUpperCase();
        if (!startsAt || startsAt > nowTs) return false;
        if (CANCELED_APPOINTMENT_STATUSES.has(status)) return false;
        return true;
      })
      .sort((a, b) => (parseDate(b.startAt)?.getTime() || 0) - (parseDate(a.startAt)?.getTime() || 0));
  }, [appointmentsSorted, nowTs]);
  const latestPastAppointment = pastAppointments[0] || null;

  const pendingTests = React.useMemo(() => {
    return data.testsAssigned.filter((item) => !DONE_TEST_STATUSES.has(String(item.status || "").toUpperCase()));
  }, [data.testsAssigned]);

  const pendingTest = pendingTests[0] || null;

  const greetingDate = React.useMemo(() => {
    return formatRuDate(new Date(), {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, []);

  const todayKey = React.useMemo(() => dayKey(new Date()), []);

  const onOpenSession = React.useCallback(
    (session) => {
      if (!session) return;
      if (session.telemostUrl) {
        if (!openExternalUrl(session.telemostUrl)) toast.error("Некорректная ссылка на встречу");
        return;
      }
      navigate("/dashboard/settings?tab=appointments");
    },
    [navigate, toast]
  );

  const openDiary = React.useCallback(
    (compose = false) => {
      navigate(compose ? "/dashboard/diary?compose=1" : "/dashboard/diary");
    },
    [navigate]
  );

  const openAppointments = React.useCallback(() => {
    navigate("/dashboard/settings?tab=appointments");
  }, [navigate]);

  const openChat = React.useCallback(() => {
    navigate("/chat");
  }, [navigate]);

  const openCatalog = React.useCallback(() => {
    navigate("/catalog");
  }, [navigate]);

  const openPrimaryPsychologistProfile = React.useCallback(() => {
    if (data.primaryPsychologist?.id) {
      navigate(`/psychologist/${data.primaryPsychologist.id}`);
      return;
    }
    navigate("/catalog");
  }, [data.primaryPsychologist?.id, navigate]);

  const openLink = React.useCallback(
    (href) => {
      const url = String(href || "").trim();
      if (!url) return;
      if (isExternalHref(url)) {
        if (!openExternalUrl(url)) toast.error("Некорректная ссылка");
        return;
      }
      navigate(url);
    },
    [navigate, toast]
  );

  const openPendingTest = React.useCallback(
    (assignment) => {
      const href = String(assignment?.launchUrl || "").trim();
      if (href) {
        if (isExternalHref(href)) {
          if (!openExternalUrl(href)) toast.error("Некорректная ссылка на тест");
          return;
        }
        navigate(href);
        return;
      }

      toast.info("Назначение теста сохранено, но отдельный экран прохождения для этого формата пока не подключен.");
    },
    [navigate, toast]
  );

  const handleSaveMood = React.useCallback(async () => {
    if (savingMood) return;

    const moodScore = diaryMoodScoreFromId(selectedMood);
    if (!Number.isFinite(moodScore)) {
      toast.error("Выберите настроение");
      return;
    }

    setSavingMood(true);
    try {
      const saved = await diaryApi.createEntry({
        title: diaryQuickEntryTitle(moodScore),
        content: diaryQuickEntryContent(moodScore, moodNote),
        moodScore,
        entryDate: dayKey(new Date()),
        shareWithGrantedPsychologists: false,
      });

      setData((prev) => ({
        ...prev,
        diaryEntries: sortDiaryEntries([
          saved,
          ...prev.diaryEntries.filter((entry) => String(entry.id) !== String(saved.id)),
        ]),
      }));
      setMoodNote("");
      toast.success("Чек-ин сохранён в дневнике");
    } catch (error) {
      toast.error(error?.message || "Не удалось сохранить запись");
    } finally {
      setSavingMood(false);
    }
  }, [moodNote, savingMood, selectedMood, toast]);

  const handleToggleTask = React.useCallback(
    async (task) => {
      const taskId = String(task?.id || "").trim();
      if (!taskId || savingTaskIds.includes(taskId)) return;

      setSavingTaskIds((prev) => [...prev, taskId]);
      try {
        const saved = task.done ? await plannerApi.reopenTask(task.id) : await plannerApi.completeTask(task.id);
        setData((prev) => ({
          ...prev,
          plannerTasks: prev.plannerTasks.map((item) =>
            String(item.id) === taskId ? { ...item, ...saved, done: saved.done } : item
          ),
        }));
      } catch (error) {
        toast.error(error?.message || "Не удалось обновить задачу");
      } finally {
        setSavingTaskIds((prev) => prev.filter((id) => id !== taskId));
      }
    },
    [savingTaskIds, toast]
  );

  const assignedPracticeTasks = React.useMemo(() => {
    return data.plannerTasks
      .filter((task) => task.assignedByPsychologistUserId || /практик|домаш|задани|упражн/i.test(`${task.title} ${task.description}`))
      .slice(0, 2);
  }, [data.plannerTasks]);

  const materialItems = React.useMemo(() => {
    const items = [];

    if (pendingTest) {
      items.push({
        id: `material-test-${pendingTest.id || pendingTest.title}`,
        icon: "🧪",
        title: pendingTest.title,
        meta: pendingTest.durationMinutes ? `Тест · около ${pendingTest.durationMinutes} мин` : "Назначенный тест",
        tag: "Тест",
        onClick: () => openPendingTest(pendingTest),
      });
    }

    assignedPracticeTasks.forEach((task, index) => {
      items.push({
        id: `material-task-${task.id || index}`,
        icon: "🧭",
        title: task.title,
        meta: task.description ? trimText(task.description, 72) : "Практика между сессиями",
        tag: task.category || "Практика",
        onClick: () => handleToggleTask(task),
      });
    });

    data.recommendations.forEach((item) => {
      items.push({
        id: `material-reco-${item.key}`,
        icon: item.emoji,
        title: item.title,
        meta: item.subtitle,
        tag: item.type,
        onClick: () => openLink(item.href),
      });
    });

    const unique = [];
    const seen = new Set();
    items.forEach((item) => {
      if (seen.has(item.id) || unique.length >= 4) return;
      seen.add(item.id);
      unique.push(item);
    });
    return unique;
  }, [assignedPracticeTasks, data.recommendations, handleToggleTask, openLink, openPendingTest, pendingTest]);

  const roleLabel = normalizedRole === "PSYCHOLOGIST" ? "Психолог" : normalizedRole === "ADMIN" ? "Администратор" : "Клиент";
  const nextAppointmentTs = parseDate(nextAppointment?.startAt)?.getTime() || 0;
  const hasSessionToday = Boolean(nextAppointment && dayKey(nextAppointment.startAt) === todayKey);
  const minutesToNextAppointment = nextAppointmentTs ? Math.max(0, Math.round((nextAppointmentTs - nowTs) / (1000 * 60))) : null;
  const heroMeta = React.useMemo(() => {
    if (!data.primaryPsychologist) {
      return {
        variant: "no-therapist",
        subtitle: "Выберите специалиста, запишитесь на первую сессию и начните вести дневник.",
        actions: [
          { label: "Подобрать психолога", kind: "primary", icon: "users", onClick: openCatalog },
          { label: "Записаться", kind: "secondary", icon: "calendar", onClick: openCatalog },
          { label: "Открыть дневник", kind: "secondary", icon: "note", onClick: () => openDiary(true) },
        ],
      };
    }

    if (hasSessionToday && nextAppointment) {
      return {
        variant: "session",
        eyebrow: "Сессия скоро начнётся",
        subtitle: `${formatRuDate(nextAppointment.startAt, { day: "2-digit", month: "long" })} с ${data.primaryPsychologist?.name || "психологом"}. До начала ${Math.max(minutesToNextAppointment || 0, 0)} минут.`,
        actions: [
          { label: "Открыть встречу", kind: "primary", icon: "link", onClick: () => onOpenSession(nextAppointment) },
          { label: "Написать психологу", kind: "secondary", icon: "chat", onClick: openChat },
          { label: "Открыть дневник", kind: "secondary", icon: "note", onClick: () => openDiary(true) },
        ],
      };
    }

    return {
      variant: "none",
      subtitle: "Можно назначить следующую сессию, написать специалисту или спокойно обновить дневник.",
      actions: [
        { label: "Записаться", kind: "primary", icon: "calendar", onClick: data.primaryPsychologist ? openPrimaryPsychologistProfile : openCatalog },
        { label: "Написать психологу", kind: "secondary", icon: "chat", onClick: openChat },
        { label: "Дневник", kind: "secondary", icon: "note", onClick: () => openDiary(false) },
      ],
    };
  }, [
    data.primaryPsychologist,
    hasSessionToday,
    minutesToNextAppointment,
    nextAppointment,
    onOpenSession,
    openCatalog,
    openChat,
    openDiary,
    openPrimaryPsychologistProfile,
  ]);
  const onboardingSteps = React.useMemo(() => {
    return (Array.isArray(data.onboarding?.steps) ? data.onboarding.steps : []).map((step, index) => ({
      id: String(step.id || `step-${index + 1}`),
      number: index + 1,
      title: step.title || `Шаг ${index + 1}`,
      desc: step.description || "",
      cta: step.ctaLabel || "Перейти",
      done: Boolean(step.completed),
      onClick: () => {
        const href = String(step.href || "").trim();
        if (!href) return;
        if (href.includes("/diary")) {
          openDiary(false);
          return;
        }
        if (href.includes("/appointments")) {
          openAppointments();
          return;
        }
        if (href === "/psychologists") {
          openCatalog();
          return;
        }
        navigate(href);
      },
    }));
  }, [data.onboarding?.steps, navigate, openAppointments, openCatalog, openDiary]);
  const onboardingDoneCount = React.useMemo(() => onboardingSteps.filter((step) => step.done).length, [onboardingSteps]);
  const onboardingProgress = React.useMemo(
    () => Math.round((onboardingDoneCount / Math.max(onboardingSteps.length, 1)) * 100),
    [onboardingDoneCount, onboardingSteps.length]
  );
  const onboardingCurrentStep = React.useMemo(
    () => onboardingSteps.find((step) => !step.done) || onboardingSteps[onboardingSteps.length - 1],
    [onboardingSteps]
  );
  const showOnboarding = onboardingShouldShow && !onboardingDismissed && onboardingSteps.length > 0;

  const handleDismissOnboarding = React.useCallback(async () => {
    if (onboardingActionLoading) return;
    setOnboardingActionLoading(true);
    try {
      const response = await onboardingApi.dismiss();
      const flags = extractOnboardingFlags(response);
      setOnboardingDismissed(flags.dismissed || !flags.shouldShow);
      setOnboardingShouldShow(flags.shouldShow);
    } catch (error) {
      toast.error(error?.message || "Не удалось скрыть подсказки");
    } finally {
      setOnboardingActionLoading(false);
    }
  }, [onboardingActionLoading, toast]);

  return (
    <div className="client-dashboard-v2 client-dashboard-v2--mock podcasts-hub">
      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="dashboard"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel={roleLabel}
          canOpenLiteratureEditor={canManageLiterature}
          notificationsBadge={unreadNotificationsCount}
          onLogout={handleLogout}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <main className="main podcasts-main cd-main">
          <div className="topbar">
            <div className="topbar-left">
              <button type="button" className="btn-icon topbar-hamburger" onClick={toggleSidebarFromTopbar} aria-label="Свернуть или открыть меню">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <button type="button" className="btn-icon topbar-sidebar-settings" onClick={openSidebarEditorFromTopbar} aria-label="Настроить меню" title="Настроить меню">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6h10" />
                  <path d="M18 6h2" />
                  <path d="M4 12h2" />
                  <path d="M10 12h10" />
                  <path d="M4 18h8" />
                  <path d="M16 18h4" />
                  <circle cx="16" cy="6" r="2" />
                  <circle cx="8" cy="12" r="2" />
                  <circle cx="14" cy="18" r="2" />
                </svg>
              </button>

              <div>
                <div className="topbar-greeting">Сегодня</div>
                <div className="topbar-date">{greetingDate}</div>
              </div>
            </div>

            <div className="topbar-right">
              <button type="button" className="btn-icon" onClick={() => navigate("/notifications")} aria-label="Уведомления" title="Уведомления">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadNotificationsCount > 0 ? <div className="notif-dot" /> : null}
              </button>
              <button type="button" className="btn-sm primary" onClick={data.primaryPsychologist ? openPrimaryPsychologistProfile : openCatalog}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {data.primaryPsychologist ? "Записаться" : "Подобрать психолога"}
              </button>
            </div>
          </div>

          <div className="page client-mock-page">
            <section className="client-mock" aria-busy={loading}>
              {showOnboarding ? (
                <section className="client-mock__onboarding">
                  <div className="client-mock__onboarding-head">
                    <div>
                      <h2>Первый старт терапии</h2>
                      <p>Подсказки помогут заполнить профиль, выбрать специалиста и начать дневник.</p>
                    </div>
                    <div className="client-mock__onboarding-progress">
                      <div className="client-mock__ring">
                        <svg width="42" height="42" viewBox="0 0 42 42" aria-hidden="true">
                          <circle cx="21" cy="21" r="17" fill="none" stroke="currentColor" strokeWidth="4" className="client-mock__ring-track" />
                          <circle
                            cx="21"
                            cy="21"
                            r="17"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeDasharray={2 * Math.PI * 17}
                            strokeDashoffset={(2 * Math.PI * 17) - ((onboardingProgress / 100) * (2 * Math.PI * 17))}
                            strokeLinecap="round"
                            transform="rotate(-90 21 21)"
                            className="client-mock__ring-fill"
                          />
                        </svg>
                        <strong>{onboardingProgress}%</strong>
                      </div>
                    <div className="client-mock__onboarding-progress-copy">
                      <strong>{onboardingDoneCount} из {onboardingSteps.length}</strong>
                      <span>шагов готово</span>
                      </div>
                      <button
                        type="button"
                        className="client-mock__text-btn"
                        onClick={handleDismissOnboarding}
                        disabled={onboardingActionLoading}
                      >
                        {onboardingActionLoading ? "Скрываем..." : "Скрыть"}
                      </button>
                    </div>
                  </div>

                  <div className="client-mock__onboarding-current">
                    <div className="client-mock__onboarding-current-main">
                      <div className="client-mock__step-num">{onboardingCurrentStep.number}</div>
                      <div>
                        <strong>Шаг {onboardingCurrentStep.number} из {onboardingSteps.length} — {onboardingCurrentStep.title.toLowerCase()}</strong>
                        <span>{onboardingCurrentStep.desc}</span>
                      </div>
                    </div>
                    <button type="button" className="btn-sm primary" onClick={onboardingCurrentStep.onClick}>
                      {onboardingCurrentStep.cta}
                    </button>
                  </div>

                  <button type="button" className="client-mock__expand" onClick={() => setOnboardingExpanded((value) => !value)}>
                    <span>{onboardingExpanded ? "Скрыть шаги" : "Показать все шаги"}</span>
                    <span className={onboardingExpanded ? "is-open" : ""}>›</span>
                  </button>

                  {onboardingExpanded ? (
                    <div className="client-mock__steps">
                      {onboardingSteps.map((step) => (
                        <button key={step.id} type="button" className={`client-mock__step ${step.done ? "is-done" : ""} ${step.id === onboardingCurrentStep.id ? "is-current" : ""}`} onClick={step.onClick}>
                          <span className="client-mock__step-check">{step.done ? "✓" : ""}</span>
                          <span>{step.title}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </section>
              ) : null}

              <section className={`client-mock__hero is-${heroMeta.variant}`}>
                <div className="client-mock__hero-main">
                  {heroMeta.variant === "session" ? (
                    <div className="client-mock__hero-eyebrow">
                      <span />
                      <small>{heroMeta.eyebrow}</small>
                    </div>
                  ) : null}
                  <h1>
                    {heroMeta.variant === "session"
                      ? `Сессия сегодня в ${nextAppointment ? formatRuTime(nextAppointment.startAt) : "—:—"}`
                      : heroMeta.variant === "none"
                        ? "Сегодня встреч нет"
                        : "Начните путь спокойно"}
                  </h1>
                  <p>{heroMeta.subtitle}</p>
                  <div className="client-mock__hero-actions">
                    {heroMeta.actions.map((action) => (
                      <button key={action.label} type="button" className={`client-mock__hero-btn ${action.kind === "primary" ? "is-primary" : "is-secondary"}`} onClick={action.onClick}>
                        {action.icon ? <MockIcon name={action.icon} size={14} /> : null}
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>

                <aside className="client-mock__hero-side">
                  <div className="client-mock__hero-side-label">Ваш специалист</div>
                  {data.primaryPsychologist ? (
                    <>
                      <div className="client-mock__hero-avatar">
                        {data.primaryPsychologist.avatarUrl ? (
                          <img src={data.primaryPsychologist.avatarUrl} alt={data.primaryPsychologist.name} loading="lazy" decoding="async" />
                        ) : (
                          initials(data.primaryPsychologist.name)
                        )}
                      </div>
                      <div className="client-mock__hero-side-name">{data.primaryPsychologist.name}</div>
                      <div className={`client-mock__hero-side-copy ${heroMeta.variant === "session" ? "is-highlight" : ""}`}>
                        {heroMeta.variant === "session" && nextAppointment
                          ? `Следующая запись: сегодня в ${formatRuTime(nextAppointment.startAt)}`
                          : "Следующая запись пока не назначена"}
                      </div>
                    </>
                  ) : (
                    <div className="client-mock__hero-side-copy">Психолог пока не выбран</div>
                  )}
                </aside>
              </section>

              <section className="client-mock__board">
                <div className="client-mock__main-column">
                  <article className="client-mock__card">
                    <header className="client-mock__card-head">
                      <MockSectionHeading icon="calendar" title="Мои записи" />
                      <button type="button" className="client-mock__card-link" onClick={openAppointments}>Все записи →</button>
                    </header>
                    {!data.primaryPsychologist ? (
                      <div className="client-mock__appointments-empty">
                        <span>Записей пока нет</span>
                        <button type="button" className="btn-sm" onClick={openCatalog}>
                          <MockIcon name="users" size={14} />
                          Подобрать психолога
                        </button>
                      </div>
                    ) : (
                      <div className="client-mock__rows">
                        {nextAppointment ? (
                          <div className="client-mock__row is-accent">
                            <div className="client-mock__row-icon">📅</div>
                            <div className="client-mock__row-body">
                              <strong>
                                Следующая встреча — {hasSessionToday ? "сегодня" : formatRuDate(nextAppointment.startAt, { day: "numeric", month: "long" })}, {formatRuTime(nextAppointment.startAt)}
                              </strong>
                              <span>{formatRuDate(nextAppointment.startAt, { day: "2-digit", month: "long" })} · {data.primaryPsychologist.name}</span>
                            </div>
                          </div>
                        ) : null}
                        {latestPastAppointment ? (
                          <div className="client-mock__row">
                            <div className="client-mock__row-icon">✓</div>
                            <div className="client-mock__row-body">
                              <strong>Последняя встреча завершена</strong>
                              <span>{formatRuDate(latestPastAppointment.startAt, { day: "2-digit", month: "long" })} · можно открыть историю или выбрать следующий слот</span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </article>

                  <article className="client-mock__card">
                    <header className="client-mock__card-head">
                      <MockSectionHeading icon="chat" title="Чат" />
                      <button type="button" className="client-mock__card-link" onClick={openChat}>Открыть →</button>
                    </header>
                    {!data.primaryPsychologist ? (
                      <div className="client-mock__muted">Чат появится после выбора психолога</div>
                    ) : (
                      <button type="button" className="client-mock__chat" onClick={openChat}>
                        <div className="client-mock__chat-avatar">{initials(data.primaryPsychologist.name)}</div>
                        <div className="client-mock__chat-body">
                          <strong>{data.primaryPsychologist.name}</strong>
                          <span>Новых сообщений: {formatInt(messageUnreadTotal)}</span>
                        </div>
                        <i />
                      </button>
                    )}
                  </article>

                  <article className="client-mock__card client-mock__card--diary">
                    <header className="client-mock__card-head">
                      <MockSectionHeading icon="note" title="Дневник" />
                      <button type="button" className="client-mock__card-link" onClick={() => openDiary(false)}>Открыть →</button>
                    </header>
                    <div className="client-mock__diary-title">Как вы сегодня?</div>
                    <div className="client-mock__diary-copy">Можно быстро отметить состояние или записать мысль, пока она не потерялась.</div>
                    <div className="client-mock__moods">
                      {DIARY_MOOD_OPTIONS.map((option) => (
                        <button key={option.id} type="button" className={`client-mock__mood ${selectedMood === option.id ? "is-active" : ""}`} onClick={() => setSelectedMood(option.id)}>
                          <span>{option.emoji}</span>
                          <small>{option.label}</small>
                        </button>
                      ))}
                    </div>
                    <input
                      className="client-mock__diary-input"
                      type="text"
                      value={moodNote}
                      onChange={(event) => setMoodNote(event.target.value)}
                      placeholder="Что сегодня повлияло на состояние? (необязательно)"
                      maxLength={180}
                    />
                    <button type="button" className="btn-sm primary client-mock__save-btn" onClick={handleSaveMood} disabled={savingMood}>
                      {savingMood ? "Сохранение..." : "Сохранить"}
                    </button>
                  </article>
                </div>

                <aside className="client-mock__rail">
                  <article className="client-mock__card">
                    <header className="client-mock__card-head">
                      <MockSectionHeading icon="users" title="Мой психолог" />
                      {data.primaryPsychologist ? (
                        <button type="button" className="client-mock__card-link" onClick={openPrimaryPsychologistProfile}>Профиль →</button>
                      ) : null}
                    </header>
                    {!data.primaryPsychologist ? (
                      <>
                        <div className="client-mock__muted client-mock__muted--spaced">Психолог пока не выбран</div>
                        <button type="button" className="btn-sm primary client-mock__full-btn" onClick={openCatalog}>
                          <MockIcon name="users" size={14} />
                          Подобрать психолога
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="client-mock__therapist-top">
                          <div className="client-mock__therapist-avatar">
                            {data.primaryPsychologist.avatarUrl ? (
                              <img src={data.primaryPsychologist.avatarUrl} alt={data.primaryPsychologist.name} loading="lazy" decoding="async" />
                            ) : (
                              initials(data.primaryPsychologist.name)
                            )}
                          </div>
                          <div className="client-mock__therapist-meta">
                            <strong className="client-mock__therapist-name">{data.primaryPsychologist.name}</strong>
                            <span className="client-mock__therapist-role">Ваш специалист</span>
                          </div>
                        </div>
                        <div className="client-mock__therapist-stats">
                          <div>
                            <strong>{formatInt(completedSessions)}</strong>
                            <span>{completedSessions === 1 ? "сессия" : "сессий"}</span>
                          </div>
                          <div>
                            <strong>—</strong>
                            <span>оценка</span>
                          </div>
                        </div>
                        <button type="button" className="btn-sm client-mock__full-btn" onClick={openPrimaryPsychologistProfile}>Профиль</button>
                      </>
                    )}
                  </article>

                  <article className="client-mock__card">
                    <header className="client-mock__card-head">
                      <MockSectionHeading icon="bell" title="Напоминания" />
                      <button type="button" className="client-mock__card-link" onClick={() => navigate("/notifications")}>Все →</button>
                    </header>
                    {!data.primaryPsychologist && !nextAppointment ? (
                      <div className="client-mock__muted">Активных напоминаний нет</div>
                    ) : (
                      <div className="client-mock__stack">
                        {data.primaryPsychologist ? (
                          <div className="client-mock__mini-card">
                            <strong>Чек-ин на сегодня</strong>
                            <span>Отметьте состояние, чтобы видеть динамику между сессиями.</span>
                            <button type="button" className="client-mock__inline-link" onClick={() => openDiary(true)}>Открыть →</button>
                          </div>
                        ) : null}
                        {nextAppointment ? (
                          <div className="client-mock__mini-card">
                            <strong>Напоминание о сессии</strong>
                            <span>Встреча сегодня в {formatRuTime(nextAppointment.startAt)} · онлайн.</span>
                            <button type="button" className="client-mock__inline-link" onClick={() => onOpenSession(nextAppointment)}>Открыть →</button>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </article>

                  <article className="client-mock__card">
                    <header className="client-mock__card-head">
                      <MockSectionHeading icon="spark" title="Материалы" />
                      <button type="button" className="client-mock__card-link" onClick={materialItems[0]?.onClick || openChat}>Открыть →</button>
                    </header>
                    {!data.primaryPsychologist ? (
                      <div className="client-mock__muted">Появятся после выбора психолога</div>
                    ) : (
                      <div className="client-mock__materials">
                        {(materialItems.length ? materialItems : []).slice(0, 2).map((item) => (
                          <button key={item.id} type="button" className="client-mock__material-line" onClick={item.onClick}>
                            <strong>{item.title}</strong>
                            <span> · {item.tag}</span>
                          </button>
                        ))}
                        {!materialItems.length ? <div className="client-mock__muted">Материалов пока нет</div> : null}
                      </div>
                    )}
                  </article>
                </aside>
              </section>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
